// context/ChildrenDetailsContext.tsx
import React, { createContext, useContext, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  Action,
  buildPermissionContext,
  Resource,
} from "../autorisations/permissionsMap";
import { useAcademicYear } from "./AcademicYearContext";
import { toast } from "sonner";
import { getStudentArchivesById } from "../../../api/archives";
import { getStudentById } from "../../../api/etudiants";
import { getParentById } from "../../../api/parents";

const ChildrenDetailsContext = createContext<{
  childrenDetails: any[];
  setChildrenDetails: React.Dispatch<React.SetStateAction<any[]>>;
  selectedChild: string | undefined;
  setSelectedChild: React.Dispatch<React.SetStateAction<string | undefined>>;
}>({
  childrenDetails: [],
  setChildrenDetails: () => {},
  selectedChild: "",
  setSelectedChild: () => {},
});

export const ChildrenDetailProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const userData = useSelector((state: any) => state.auth.user);
  const userType = useSelector((state: any) => state.auth.userType);

  const { selectedYear } = useAcademicYear();
  const [childrenParentDetail, setChildrenParentDetail] = useState<any[]>([]);

  const [selectedChild, setSelectedChild] = useState<string>();

  // on récupère la liste des enfants du ParentDetail
  const getParentChildren = async () => {
    try {
      const response = await getParentById(userData?.id);
      // console.log("Enfants du parent: ", response.data.children);
      // setChildrenParentDetail(response.data.children);
      const children = response.data.children;
      if (children) {
        const childrenStudentInfo = await Promise.all(
          children.map(async (child: any) => {
            const studentInfo = await getChildStudentInfo(child.id);
            return {
              ...child,
              blocked_Note: studentInfo?.blocked_Note,
              statut: studentInfo?.statut,
              sexe: studentInfo?.sexe,
              class: studentInfo?.class,
              academicYear: studentInfo?.academicYear,
              id_academicYear: studentInfo?.id_academicYear,
            };
          }),
        );
        // console.log("infos child final: ", childrenStudentInfo);
        // if (childrenStudentInfo.length > 0)
        //   setSelectedChild(childrenStudentInfo[0]?.id);

        setChildrenParentDetail(childrenStudentInfo);
      } else {
        setChildrenParentDetail([]);
      }
    } catch (error: any) {
      console.log(
        "Erreur lors de la récupération des enfants du parents: ",
        error,
      );
      toast.error(
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  const getChildStudentInfo = async (id_child: string) => {
    try {
      const student = await getStudentById(
        id_child,
        buildPermissionContext(Resource.PARENT, Action.READ),
      );
      const studentArchive = await getStudentArchivesById(
        id_child,
        buildPermissionContext(Resource.PARENT, Action.READ),
      );
      if (!selectedYear) return;
      // console.log("arch student: ",studentArchive)
      const studentArchData = studentArchive.data.find(
        (archiveItem: any) => archiveItem?.id_academicYear === selectedYear.id,
      );
      if (studentArchData) {
        const formattedData = selectedYear
          ? {
              ...student.data,
              blocked_Note: studentArchData?.blocked_Note,
              class: studentArchData?.Class,
              academicYear: studentArchData?.AcademicYear,
              id_academicYear: studentArchData?.id_academicYear,
            }
          : { ...student.data };
        // console.log("est inscrit: ", formattedData);
        // setStudentData(formattedData);
        return formattedData;
      } else {
        const formattedData = {
          ...student.data,
          class: studentArchive.data[studentArchive?.data.length - 1]?.Class,
          academicYear:
            studentArchive.data[studentArchive?.data.length - 1]?.AcademicYear,
          id_academicYear:
            studentArchive.data[studentArchive?.data.length - 1]
              ?.id_academicYear,
        };
        // console.log("non inscript", formattedData);
        return formattedData;
      }
    } catch (error: any) {
      // console.error(
      //   "Erreur lors de la récupération des infos étudiant de l'enfant:",
      //   error,
      // );
      toast.error(
        error.message ||
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  // charger les infos des etudiants liees a ce parent
  useEffect(() => {
    if (userType !== "ParentProfil" || !selectedYear) return;
    getParentChildren();
    // console.log("UserData: ", userData);
  }, [selectedYear]);

  // charger la valeur de l'enfant selectionner dans le localStorage
  useEffect(() => {
    if (userType !== "ParentProfil") return;
    const stored = localStorage.getItem("selectedChild");
    if (stored) {
      setSelectedChild(JSON.parse(stored));
      return;
    }
    if (childrenParentDetail.length > 0)
      setSelectedChild(childrenParentDetail[0]?.id);
    // console.log("Selected Child: ", selectedChild);
  }, []);

  // Save to localStorage when value changes
  useEffect(() => {
    if (selectedChild) {
      localStorage.setItem("selectedChild", JSON.stringify(selectedChild));
    }
  }, [selectedChild]);

  return (
    <ChildrenDetailsContext.Provider
      value={{
        childrenDetails: childrenParentDetail,
        setChildrenDetails: setChildrenParentDetail,
        selectedChild,
        setSelectedChild,
      }}
    >
      {children}
    </ChildrenDetailsContext.Provider>
  );
};

// Hook personnalisé pour utiliser le contexte plus facilement
export const useChildrenDetails = () => {
  const context = useContext(ChildrenDetailsContext);
  if (!context) {
    throw new Error("usePermissions must be used within a PermissionsProvider");
  }
  return context;
};
